import React, { useState } from 'react';
import { Alert, Button, Modal, notification, Table, Typography } from 'antd';
import { BlockingPackage, MassiveConflictError } from 'services/MassiveErrors';
import { Massive } from 'services/MassiveService';
import { useCancelMassive } from 'queries/useMassiveQuery';
import { MassiveErrorAlert, notifyMassiveError } from './serviceError';

const { Paragraph } = Typography;

interface Props {
  open: boolean;
  massive?: Massive;
  error?: unknown;
  statusName: (status: number) => string;
  onClose: () => void;
}

const blockingColumns = (statusName: (status: number) => string) => [
  { title: 'Nro. seguimiento', dataIndex: 'tracking' },
  {
    title: 'Estado',
    dataIndex: 'status',
    render: (status: number) => statusName(status),
  },
];

const MassiveDeleteModal = ({
  open,
  massive,
  error,
  statusName,
  onClose,
}: Props) => {
  const [blocking, setBlocking] = useState<BlockingPackage[]>([]);
  const cancelMassive = useCancelMassive();

  const close = () => {
    setBlocking([]);
    onClose();
  };

  const remove = async (keepBlocked: boolean) => {
    if (!massive) return;
    try {
      await cancelMassive.mutateAsync({
        massiveId: massive.massive_id,
        keepBlocked,
      });
      notification.success({
        message: 'Listo',
        description: keepBlocked
          ? 'Se eliminaron los paquetes que todavía no se operaron. La masiva sigue disponible con el resto.'
          : 'La masiva se eliminó y sus paquetes quedaron cancelados.',
      });
      close();
    } catch (failure) {
      if (failure instanceof MassiveConflictError) {
        setBlocking(failure.blocking);
        return;
      }
      notifyMassiveError(failure, 'No se pudo eliminar');
    }
  };

  const confirmPartial = () =>
    Modal.confirm({
      title: 'Eliminar sólo los que no se operaron',
      okText: 'Eliminar',
      okButtonProps: { danger: true },
      cancelText: 'Volver',
      content: `Se van a eliminar ${
        (massive?.created_count ?? 0) - blocking.length
      } paquetes. Los ${blocking.length} que ya se operaron quedan como están y la masiva sigue disponible.`,
      onOk: () => remove(true),
    });

  if (blocking.length) {
    return (
      <Modal
        open={open}
        title="No se puede eliminar la masiva completa"
        onCancel={close}
        footer={[
          <Button key="close" onClick={close}>
            Volver
          </Button>,
          <Button
            key="partial"
            danger
            type="primary"
            loading={cancelMassive.isPending}
            onClick={confirmPartial}
          >
            Eliminar todos los no-operados
          </Button>,
        ]}
      >
        <Alert
          type="warning"
          showIcon
          message={`${blocking.length} ${
            blocking.length === 1
              ? 'paquete ya empezó'
              : 'paquetes ya empezaron'
          } a operarse`}
          description="Esos no se pueden eliminar. Sí podés eliminar el resto, y la masiva queda con los que ya se operaron."
          style={{ marginBottom: 16 }}
        />
        <Table
          size="small"
          rowKey="tracking"
          columns={blockingColumns(statusName)}
          dataSource={blocking}
          pagination={false}
        />
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      title="Eliminar la masiva"
      okText="Eliminar la masiva"
      okButtonProps={{
        danger: true,
        loading: cancelMassive.isPending,
        disabled: !massive,
      }}
      cancelText="Cancelar"
      onCancel={close}
      onOk={() => remove(false)}
    >
      {error ? (
        <MassiveErrorAlert error={error} title="No se pudo cargar la masiva" />
      ) : null}
      <Alert
        type="warning"
        showIcon
        message="Esta acción no se puede deshacer"
        description={`Vas a eliminar la masiva ${massive?.massive_id ?? ''}, con ${massive?.created_count ?? 0} paquetes creados.`}
        style={{ marginBottom: 16 }}
      />
      <Paragraph>
        Si alguno de los paquetes ya se empezó a operar, no se puede eliminar la
        masiva completa. En ese caso te vamos a mostrar cuáles son y vas a poder
        eliminar solamente los que todavía no se operaron.
      </Paragraph>
      <Alert
        type="info"
        showIcon
        message="Los paquetes eliminados liberan su Nro Seguimiento, así que podés corregir el archivo y volver a subirlo."
      />
    </Modal>
  );
};

export default MassiveDeleteModal;
